/**
 * SubmittingScreen — Dramatic dark-mode processing animation.
 *
 * Features:
 *  • Animated SVG progress ring with gradient stroke
 *  • Floating particles (framer-motion)
 *  • Step-by-step reveal with glow effects
 *  • Pulsing gradient blobs in background
 *  • Percentage counter synced to step progress
 */

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Zap,
  Search,
  UserPlus,
  Target,
  ShieldCheck,
} from "lucide-react";

// ─── Step config ──────────────────────────────────────────────────────────
interface Step {
  label: string;
  icon: React.ReactNode;
  duration: number; // ms before moving to next
}

const STEPS: Step[] = [
  { label: "Validating submission", icon: <ShieldCheck className="w-4 h-4" />, duration: 600 },
  { label: "Enriching with Amplemarket", icon: <Zap className="w-4 h-4" />, duration: 800 },
  { label: "Looking up Salesforce Account", icon: <Search className="w-4 h-4" />, duration: 700 },
  { label: "Creating Contact", icon: <UserPlus className="w-4 h-4" />, duration: 700 },
  { label: "Creating Opportunity", icon: <Target className="w-4 h-4" />, duration: 900 },
];

// ─── Floating particles ───────────────────────────────────────────────────
function FloatingParticles() {
  const particles = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        duration: Math.random() * 4 + 3,
        delay: Math.random() * 2,
        opacity: Math.random() * 0.4 + 0.1,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: `rgba(117, 115, 247, ${p.opacity})`,
            boxShadow: `0 0 ${p.size * 3}px rgba(117, 115, 247, ${p.opacity * 0.5})`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [p.opacity, p.opacity * 1.8, p.opacity],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// ─── Progress Ring ────────────────────────────────────────────────────────
function ProgressRing({ progress }: { progress: number }) {
  const radius = 62;
  const stroke = 4;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative w-40 h-40">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140">
        {/* Background track */}
        <circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke="rgba(117, 115, 247, 0.08)"
          strokeWidth={stroke}
        />
        {/* Animated progress arc */}
        <defs>
          <linearGradient id="ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#05FAFC" />
            <stop offset="50%" stopColor="#7573F7" />
            <stop offset="100%" stopColor="#C3A0F2" />
          </linearGradient>
        </defs>
        <motion.circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke="url(#ring-gradient)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
        {/* Glow overlay */}
        <motion.circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke="url(#ring-gradient)"
          strokeWidth={stroke + 6}
          strokeLinecap="round"
          strokeDasharray={circumference}
          opacity={0.15}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ filter: "blur(6px)" }}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.div
          className="text-3xl font-extrabold text-white tabular-nums"
          key={Math.floor(progress)}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          {Math.floor(progress)}%
        </motion.div>
        <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5">
          Processing
        </div>
      </div>
    </div>
  );
}

// ─── Step Row ─────────────────────────────────────────────────────────────
function StepRow({
  step,
  state,
  index,
}: {
  step: Step;
  state: "done" | "active" | "pending";
  index: number;
}) {
  return (
    <motion.div
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${
        state === "active"
          ? "bg-white/[0.04] border border-white/[0.08]"
          : state === "done"
          ? "bg-white/[0.02]"
          : ""
      }`}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08, duration: 0.3 }}
    >
      {/* Status icon */}
      <div className="w-7 h-7 flex items-center justify-center shrink-0">
        <AnimatePresence mode="wait">
          {state === "done" ? (
            <motion.div
              key="done"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", bounce: 0.5, duration: 0.4 }}
            >
              <CheckCircle2
                className="w-5 h-5 text-[#05FAFC]"
                style={{ filter: "drop-shadow(0 0 6px rgba(5,250,252,0.4))" }}
              />
            </motion.div>
          ) : state === "active" ? (
            <motion.div
              key="active"
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              transition={{ rotate: { duration: 2, repeat: Infinity, ease: "linear" }, scale: { duration: 0.2 } }}
            >
              <Loader2 className="w-5 h-5 text-[#7573F7]" />
            </motion.div>
          ) : (
            <motion.div
              key="pending"
              className="w-5 h-5 rounded-full border border-white/10"
            />
          )}
        </AnimatePresence>
      </div>

      {/* Step icon + label */}
      <div
        className={`flex items-center gap-2.5 flex-1 text-sm font-medium transition-colors ${
          state === "done"
            ? "text-white/60"
            : state === "active"
            ? "text-white"
            : "text-white/25"
        }`}
      >
        <span className={state === "active" ? "text-[#7573F7]" : state === "done" ? "text-[#05FAFC]/60" : "text-white/15"}>
          {step.icon}
        </span>
        {step.label}
      </div>

      {/* Done timestamp feel */}
      {state === "done" && (
        <motion.span
          className="text-[10px] text-white/20 font-mono"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          done
        </motion.span>
      )}
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────
export function SubmittingScreen() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    let elapsed = 0;
    const timers = STEPS.map((step, i) => {
      elapsed += step.duration;
      return setTimeout(() => setActive(i + 1), elapsed);
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  // Progress: 0-100 scaled across steps
  const progress = Math.min((active / STEPS.length) * 100, 100);

  return (
    <div className="relative min-h-[480px] flex flex-col items-center justify-center py-12 px-4 overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-violet-500/8 rounded-full filter blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/8 rounded-full filter blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-fuchsia-500/5 rounded-full filter blur-[80px] animate-pulse" style={{ animationDelay: "0.5s" }} />
      </div>

      {/* Particles */}
      <FloatingParticles />

      <motion.div
        className="relative z-10 flex flex-col items-center gap-8 w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Progress ring */}
        <ProgressRing progress={progress} />

        {/* Title */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-xl font-extrabold text-white">
            Sending to Salesforce
          </h2>
          <p className="text-sm text-white/30 mt-1">
            Don't close this tab
          </p>
        </motion.div>

        {/* Steps list */}
        <div className="w-full space-y-1.5 backdrop-blur-2xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-4">
          {STEPS.map((step, i) => {
            const state = i < active ? "done" : i === active ? "active" : "pending";
            return <StepRow key={step.label} step={step} state={state} index={i} />;
          })}
        </div>

        {/* Subtle brand accent */}
        <motion.div
          className="flex items-center gap-2 text-white/20 text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Zap className="w-3 h-3" />
          <span>Powered by Channelscaler</span>
        </motion.div>
      </motion.div>
    </div>
  );
}
